import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pause, Play, SkipForward, RotateCcw, X, KeyRound, Wallet, Users, Truck, Briefcase, Trophy } from 'lucide-react';
import useDemoPlayer from '../hooks/useDemoPlayer';
import useDemoTracker from '../hooks/useDemoTracker';
import DemoSceneRenderer from '../components/demo/DemoSceneRenderer';
import DemoContactForm from '../components/demo/DemoContactForm';
import HSTLogo from '../components/HSTLogo';
import { formatMoney } from '../utils/format';
import { useTranslation } from '../i18n';

/**
 * Public demo route — scripted 2-minute walkthrough of HST core mechanics.
 * No license required. Available at /demo.
 */
export default function Demo() {
  const navigate = useNavigate();
  const player = useDemoPlayer();
  const { track } = useDemoTracker();
  const { t } = useTranslation();
  const { scene, sceneIdx, sceneCount, mockState, paused, completed, progressPct, advance, restart, pause, resume } = player;

  // Fire 'demo_started' once on mount.
  useEffect(() => { track('demo_started'); }, [track]);

  // Fire 'demo_completed' when the user reaches the contact scene.
  useEffect(() => {
    if (scene?.kind === 'contact') track('demo_completed');
  }, [scene, track]);

  return (
    <div className="min-h-screen bg-[#0B1220] text-foreground relative overflow-hidden">
      {/* Backdrop deco */}
      <div className="absolute inset-0 bg-gradient-radial from-accent/10 via-transparent to-transparent pointer-events-none" />
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, white 1px, transparent 1px)', backgroundSize: '24px 24px' }}
      />

      <DemoHeader onExit={() => navigate('/')} />

      {/* Progress bar */}
      <div className="px-6 max-w-3xl mx-auto" data-testid="demo-progress">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">
          <span>{t('demo_step_label')} {Math.min(sceneIdx + 1, sceneCount)} / {sceneCount}</span>
          <span>{progressPct}%</span>
        </div>
        <div className="h-1 rounded-full bg-ink-700 overflow-hidden">
          <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Stat bar */}
      <StatBar mock={mockState} />

      {/* Scene area */}
      <main className="px-4 mt-6 max-w-3xl mx-auto pb-32">
        {scene && scene.kind !== 'contact' && (
          <>
            <Narrator text={t(scene.narratorKey)} key={`narrator-${sceneIdx}`} />
            <div className="mt-4">
              <DemoSceneRenderer scene={scene} />
            </div>
            {scene.cta && (
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={advance}
                  className="bg-accent hover:bg-accent-hover text-white px-5 py-2.5 rounded-full text-sm font-bold uppercase tracking-wider transition-colors shadow-lg shadow-accent/30 animate-in fade-in slide-in-from-bottom-2 duration-700"
                  data-testid="demo-scene-cta"
                >
                  {t(scene.cta.labelKey)}
                </button>
              </div>
            )}
          </>
        )}

        {(scene?.kind === 'contact' || completed) && (
          <div className="mt-4 space-y-4">
            <Narrator text={t('demo_contact_intro')} />
            <DemoContactForm onRestart={restart} />
          </div>
        )}
      </main>

      <DemoControls
        paused={paused} completed={completed}
        onPause={pause} onResume={resume} onSkip={advance} onRestart={restart}
      />
    </div>
  );
}

function DemoHeader({ onExit }) {
  const { t } = useTranslation();
  return (
    <header className="px-6 py-4 flex items-center justify-between max-w-3xl mx-auto">
      <div className="flex items-center gap-2.5">
        <HSTLogo size={28} withGlow={false} />
        <div>
          <p className="font-display font-black text-foreground tracking-tight leading-none">HST</p>
          <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500 font-mono">{t('demo_subtitle')}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onExit}
        className="text-xs text-slate-400 hover:text-foreground border border-ink-600 hover:border-ink-500 rounded-full px-3 py-1.5 flex items-center gap-1.5 transition-colors"
        data-testid="demo-exit"
      >
        <KeyRound className="w-3 h-3" /> {t('demo_already_have_license')}
        <X className="w-3 h-3 opacity-50" />
      </button>
    </header>
  );
}

function StatBar({ mock }) {
  const { t } = useTranslation();
  return (
    <div className="mt-4 px-4 max-w-3xl mx-auto" data-testid="demo-stat-bar">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        <Stat icon={Wallet} label={t('demo_stat_treasury')} value={`${formatMoney(mock.treasury)} €`} tone="emerald" testid="stat-treasury" />
        <Stat icon={Truck} label={t('demo_stat_vehicles')} value={mock.vehicles} tone="accent" testid="stat-vehicles" />
        <Stat icon={Users} label={t('demo_stat_drivers')} value={mock.drivers} tone="accent" testid="stat-drivers" />
        <Stat icon={Briefcase} label={t('demo_stat_contracts')} value={mock.contracts} tone="purple" testid="stat-contracts" />
        <Stat icon={Trophy} label={t('demo_stat_reputation')} value={`${mock.reputation}/100`} tone="amber" testid="stat-reputation" />
        <Stat icon={Trophy} label={t('demo_stat_achievements')} value={mock.achievements} tone="amber" testid="stat-achievements" />
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const toneMap = {
    accent: 'text-accent', emerald: 'text-emerald-300',
    amber: 'text-amber-300', purple: 'text-purple-300',
  };
  return (
    <div className="px-2 py-1.5 rounded-md bg-ink-800/70 border border-ink-600 backdrop-blur-sm" data-testid={testid}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500 font-mono">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <p className={`text-sm font-bold font-mono ${toneMap[tone] || 'text-foreground'}`}>{value}</p>
    </div>
  );
}

function Narrator({ text }) {
  return (
    <div
      className="rounded-xl border border-accent/40 bg-accent/10 backdrop-blur-sm px-4 py-3 max-w-2xl mx-auto animate-in fade-in slide-in-from-top-2 duration-500"
      data-testid="demo-narrator"
    >
      <p className="text-sm text-slate-200 leading-relaxed text-center italic">
        « {text} »
      </p>
    </div>
  );
}

function DemoControls({ paused, completed, onPause, onResume, onSkip, onRestart }) {
  const { t } = useTranslation();
  if (completed) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-ink-800/95 border border-ink-600 rounded-full px-3 py-1.5 backdrop-blur-md shadow-2xl" data-testid="demo-controls">
        <ControlBtn icon={RotateCcw} label={t('demo_control_restart')} onClick={onRestart} testid="demo-control-restart" />
      </div>
    );
  }
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-ink-800/95 border border-ink-600 rounded-full px-3 py-1.5 backdrop-blur-md shadow-2xl" data-testid="demo-controls">
      {paused ? (
        <ControlBtn icon={Play} label={t('demo_control_resume')} onClick={onResume} testid="demo-control-resume" />
      ) : (
        <ControlBtn icon={Pause} label={t('demo_control_pause')} onClick={onPause} testid="demo-control-pause" />
      )}
      <ControlBtn icon={SkipForward} label={t('demo_control_skip')} onClick={onSkip} testid="demo-control-skip" />
      <ControlBtn icon={RotateCcw} label={t('demo_control_restart')} onClick={onRestart} testid="demo-control-restart" />
    </div>
  );
}

function ControlBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[10px] uppercase tracking-wider text-slate-300 hover:text-foreground hover:bg-ink-700 rounded-full px-2.5 py-1 flex items-center gap-1.5 transition-colors font-mono"
      data-testid={testid}
    >
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}
